import {
  Button,
  Checkbox,
  Group,
  NumberInput,
  Stack,
  Table,
  Text,
  TextInput,
  Textarea,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { IconPlus } from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { DataTableShell, PageHeader, StatusBadge } from "../../components/ui";
import { adminApi } from "../../lib/api";
import { ApiError } from "../../lib/apiClient";
import { NAV_ICONS } from "../../lib/navCopy";
import { formatDate } from "../../lib/format";

export function AdminConsultancyRateCardsPage() {
  const { companyId } = useParams<{ companyId: string }>();
  const queryClient = useQueryClient();
  const [creating, setCreating] = useState(false);

  const cardsQuery = useQuery({
    queryKey: ["admin-rate-cards", companyId],
    queryFn: () => adminApi.listConsultancyRateCards(companyId!),
    enabled: !!companyId,
  });

  const companiesQuery = useQuery({
    queryKey: ["admin-companies"],
    queryFn: () => adminApi.listCompanies({ page_size: 100 }),
  });

  const company = useMemo(
    () => (companiesQuery.data?.items ?? []).find((c) => c.id === companyId),
    [companiesQuery.data, companyId],
  );

  const form = useForm({
    initialValues: {
      name: "Standard",
      recruiter_fee_pct: 80,
      sourcing_fee_pct: 10,
      platform_fee_pct: 10,
      effective_from: new Date().toISOString().slice(0, 10),
      notes: "",
      is_default: true,
    },
  });

  const splitSum =
    Number(form.values.recruiter_fee_pct || 0) +
    Number(form.values.sourcing_fee_pct || 0) +
    Number(form.values.platform_fee_pct || 0);

  const createMutation = useMutation({
    mutationFn: () =>
      adminApi.createConsultancyRateCard(companyId!, {
        name: form.values.name,
        recruiter_fee_pct: Number(form.values.recruiter_fee_pct),
        sourcing_fee_pct: Number(form.values.sourcing_fee_pct),
        platform_fee_pct: Number(form.values.platform_fee_pct),
        effective_from: form.values.effective_from,
        notes: form.values.notes || undefined,
        is_default: form.values.is_default,
        status: "active",
      }),
    onSuccess: () => {
      notifications.show({ color: "green", message: "Rate card created" });
      queryClient.invalidateQueries({ queryKey: ["admin-rate-cards", companyId] });
      setCreating(false);
    },
    onError: (err) => {
      notifications.show({
        color: "red",
        message: err instanceof ApiError ? err.message : "Failed to create rate card",
      });
    },
  });

  const cards = cardsQuery.data ?? [];

  return (
    <Stack gap="lg">
      <PageHeader
        icon={NAV_ICONS.rateCard}
        title={`${company?.display_name ?? "Consultancy"}: Consultancy rate cards`}
        subtitle="Standing split applied when this consultancy's recruiters make a placement."
        actions={
          <Group>
            <Button component={Link} to="/admin/companies/consultancies" variant="default">
              Back to companies
            </Button>
            <Button leftSection={<IconPlus size="var(--axio-unit-16)" />} onClick={() => setCreating((v) => !v)}>
              {creating ? "Cancel" : "New rate card"}
            </Button>
          </Group>
        }
      />

      {creating && (
        <form onSubmit={form.onSubmit(() => createMutation.mutate())}>
          <Stack gap="sm">
            <TextInput label="Name" {...form.getInputProps("name")} />
            <Group grow>
              <NumberInput label="Consultancy %" min={0} max={100} {...form.getInputProps("recruiter_fee_pct")} />
              <NumberInput label="Sourcing %" min={0} max={100} {...form.getInputProps("sourcing_fee_pct")} />
              <NumberInput label="Platform %" min={0} max={100} {...form.getInputProps("platform_fee_pct")} />
            </Group>
            <Text size="sm" c={splitSum === 100 ? "teal" : "red"} fw="var(--axio-font-weight-semibold)">
              Total: {splitSum}% {splitSum === 100 ? "✓" : "(must equal 100)"}
            </Text>
            <TextInput type="date" label="Effective from" {...form.getInputProps("effective_from")} />
            <Textarea label="Notes" {...form.getInputProps("notes")} />
            <Checkbox label="Default" {...form.getInputProps("is_default", { type: "checkbox" })} />
            <Button type="submit" disabled={splitSum !== 100} loading={createMutation.isPending}>
              Save rate card
            </Button>
          </Stack>
        </form>
      )}

      <DataTableShell
        loading={cardsQuery.isLoading}
        error={
          cardsQuery.isError
            ? cardsQuery.error instanceof Error
              ? cardsQuery.error.message
              : undefined
            : undefined
        }
        onRetry={() => void cardsQuery.refetch()}
        isEmpty={cards.length === 0}
        empty={{
          icon: NAV_ICONS.rateCard,
          title: "No rate cards",
          description: "Create an active rate card before this consultancy can be paid.",
        }}
      >
        <Table verticalSpacing="md" highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Name</Table.Th>
              <Table.Th>Split</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th>Effective</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {cards.map((card) => (
              <Table.Tr key={card.id}>
                <Table.Td>
                  {card.name}
                  {card.is_default ? " (default)" : ""}
                </Table.Td>
                <Table.Td>
                  {card.recruiter_fee_pct}/{card.sourcing_fee_pct}/{card.platform_fee_pct}
                </Table.Td>
                <Table.Td>
                  <StatusBadge status={card.status} />
                </Table.Td>
                <Table.Td>{formatDate(card.effective_from)}</Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </DataTableShell>
    </Stack>
  );
}
